import type { ReactNode } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ChatHistory } from '@trinity/chat';
import { LinkContext } from '@trinity/chat-components/components/BrandInsights/context';
import { ActiveChatSessionContext } from '@trinity/chat-components/components/Chat';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { FetchMoreHistoryContext, ChatHistoryFilterContext, ChatHistoryManagerContext, ChatHistoryItemsContext } from './context';

type Props = {
  children: ReactNode;
};

export const ChatHistoryContainer = (props: Props) => {
  const historyManager = useContext(ChatHistoryManagerContext);
  const historyItems = useContext(ChatHistoryItemsContext);
  const dispatch = useContext(ChatStateDispatchContext);

  const chat = useContext(ActiveChatSessionContext);
  const activeBrandIdentifier = useActiveBrandIdentifier();
  const link = useContext(LinkContext);
  const [filter, setFilter] = useState<'all' | 'bookmarked' | 'profile'>('all');
  const [name, setName] = useState('');

  const getIncludeAdditional = useCallback(() => {
    if (!link?.initialized && link?.chatIdentifier && !historyItems.map(i => i.identifier).includes(link.chatIdentifier)) {
      return [link.chatIdentifier];
    }
    return null;
  }, [historyItems, link?.initialized, link?.chatIdentifier]);

  const queryKey = useMemo(() => {
    return {
      mode: filter === 'profile' ? 'page' : 'all',
      cursor: historyManager.cursor,
      pageSize: 20,
      includeAdditional: getIncludeAdditional(),
      context: chat.context,
      brandIdentifier: activeBrandIdentifier,
    };
  }, [historyManager.cursor, getIncludeAdditional, chat.context, activeBrandIdentifier, filter]);

  const apiClient = useChatApiClient();
  const queryClient = useQueryClient();

  // Refetch function that always uses the current queryKey from state
  const refetch = useCallback(() => {
    dispatch({ type: 'chat-loading-started' });
    return queryClient.fetchQuery({
      queryKey: [`chat:get:history`, queryKey] as const,
      queryFn: () => {
        return apiClient.chat.getChatHistory({
          mode: queryKey.mode as ChatHistory.Mode,
          cursor: queryKey.cursor,
          pageSize: queryKey.pageSize,
          includeAdditional: queryKey.includeAdditional,
          context: queryKey.context,
          brandIdentifier: queryKey.brandIdentifier,
        }).then(data => {
          // Use offset-based pagination: cursor is the count of items already loaded
          const currentOffset = queryKey.cursor || 0;
          const newCursor = currentOffset + data.items.length;
          dispatch({
            type: queryKey.cursor ? 'chat-history-page-loaded' : 'chat-history-loaded',
            payload: {
              cursor: newCursor,
              hasMore: data.hasMore,
              items: data.items,
              shared: data.shared,
            },
          });
          return data;
        }).catch(err => {
          console.error('Error fetching chat history:', err);
          dispatch({ type: 'chat-loading-errored' });
          throw err;
        });
      },
    });
  }, [apiClient, dispatch, queryClient, queryKey]);

  useEffect(() => {
    const shouldRefetch = !historyManager.initialized && !!chat.context;

    if (shouldRefetch) {
      refetch();
    }
  }, [activeBrandIdentifier, chat.brandIdentifier, chat.context, historyManager.initialized, refetch]);

  const handleFilterChange = useCallback((value: 'all' | 'bookmarked' | 'profile') => {
    if (value === filter) {
      setFilter('all');
    } else if (value === 'profile') {
      setFilter('profile');
    } else {
      setFilter(value);
    }
  }, [filter]);

  const ctx = {
    filter,
    name,
    setFilter: handleFilterChange,
    setName,
  };

  return (
    <ChatHistoryFilterContext.Provider value={ctx}>
      <FetchMoreHistoryContext.Provider value={refetch}>
        {props.children}
      </FetchMoreHistoryContext.Provider>
    </ChatHistoryFilterContext.Provider>
  );
};